import { useEffect, useRef } from "react";

/** The only decorative motion: brand arcs complete after a confirmed approval. */
export function ECompletes({ onComplete }: { onComplete: () => void }) {
  const root = useRef<HTMLDivElement>(null);
  const done = useRef(onComplete);
  done.current = onComplete;
  useEffect(() => {
    const nodes = [...root.current!.querySelectorAll(".e-arc, .e-tick")];
    if (
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ||
      !nodes[0]?.animate
    ) {
      // The completed mark remains readable, with no movement.
      const timer = window.setTimeout(() => done.current(), 600);
      return () => window.clearTimeout(timer);
    }
    let current = true;
    const animations = nodes.map((node) =>
      node.animate(
        node.classList.contains("e-tick")
          ? [
              { strokeDashoffset: 1, offset: 0 },
              { strokeDashoffset: 1, offset: 0.4 },
              { strokeDashoffset: 0, offset: 1 },
            ]
          : [
              { strokeDashoffset: 1 },
              { strokeDashoffset: 0, offset: 0.75 },
              { strokeDashoffset: 0 },
            ],
        { duration: 600, easing: "ease-out", fill: "both" },
      ),
    );
    void Promise.all(animations.map((animation) => animation.finished)).then(
      () => {
        if (current) done.current();
      },
      () => {},
    );
    return () => {
      current = false;
      animations.forEach((animation) => animation.cancel());
    };
  }, []);
  return (
    <div
      ref={root}
      role="status"
      className="e-completes flex items-center gap-4"
      aria-label="Approved"
    >
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="-5 -15 130 130"
        className="size-20 shrink-0"
      >
        <g
          fill="none"
          strokeWidth="18"
          strokeLinecap="butt"
          strokeLinejoin="round"
        >
          {[1, 2, 3, 4].map((arc) => (
            <use
              key={arc}
              className={`e-arc e-arc-${arc}`}
              href={`/brand/emdur-e-icon-colour.svg#arc-${arc}`}
            />
          ))}
        </g>
        <path
          className="e-tick"
          d="M39 48 L54 63 L80 36"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength="1"
        />
      </svg>
      <span>
        <span className="block text-lg font-strong">Approved</span>
        <span className="text-sm text-muted">Your decision is recorded.</span>
      </span>
    </div>
  );
}
